import type { PatientCardSortOrder } from '@/contexts/screenConfiguration.types';

export const DEFAULT_PATIENT_CARD_SORT_ORDER: PatientCardSortOrder = 'DEFAULT';
export const PATIENT_CARD_SCHEDULED_OR_COLUMN_WIDTH = '4.75rem';
export const PATIENT_CARD_SCHEDULED_OR_LARGE_COLUMN_WIDTH = '7rem';

type PatientCardProcedure = {
  startTime?: string | null;
  startTimeText?: string | null;
  endTimeText?: string | null;
  patientAge?: string | number | null;
  patientSex?: string | null;
  or?: string | null;
  operationRoom?: { name?: string | null } | null;
};

type PatientCardPatient = {
  id?: string | number | null;
  changedRoomAt?: string | null;
  procedure?: PatientCardProcedure | string | null;
  procedureData?: PatientCardProcedure | null;
};

const normalizeDisplayValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const normalized = String(value).trim();
  return normalized && normalized !== '-' && normalized !== '–' ? normalized : null;
};

const getPatientCardProcedure = (patient: PatientCardPatient | null | undefined): PatientCardProcedure | null => {
  if (!patient) return null;
  if (patient.procedureData) return patient.procedureData;
  return patient.procedure && typeof patient.procedure === 'object' ? patient.procedure : null;
};

export const formatPatientCardAgeAndSex = (patient: PatientCardPatient | null | undefined): string => {
  const procedure = getPatientCardProcedure(patient);
  return [normalizeDisplayValue(procedure?.patientAge), normalizeDisplayValue(procedure?.patientSex)]
    .filter(Boolean)
    .join(' ');
};

export const getPatientCardProcedureTimeParts = (
  patient: PatientCardPatient | null | undefined
): { startTime: string | null; endTime: string | null } => {
  const procedure = getPatientCardProcedure(patient);
  return {
    startTime: normalizeDisplayValue(procedure?.startTimeText),
    endTime: normalizeDisplayValue(procedure?.endTimeText),
  };
};

export const formatPatientCardScheduledOr = (patient: PatientCardPatient | null | undefined): string => {
  const procedure = getPatientCardProcedure(patient);
  return normalizeDisplayValue(procedure?.operationRoom?.name) ?? normalizeDisplayValue(procedure?.or) ?? '';
};

// Longest label that reliably fits the scheduled-OR column without abbreviation:
// the 4.75rem column at the card's 1.125rem font holds roughly 8 average glyphs,
// so 7 leaves headroom for wide characters.
const SCHEDULED_OR_MAX_UNABBREVIATED_LENGTH = 7;

const COLLAPSE_TO_INITIAL = new Set(['room', 'rooms', 'proc', 'procs']);
const ROOM_FILLER = new Set(['room', 'rooms']);

const abbreviateWord = (token: string): string => {
  if (token.length <= 2) return token.toUpperCase();

  if (COLLAPSE_TO_INITIAL.has(token.toLowerCase())) return token[0].toUpperCase();

  if (token.length <= 5 && token === token.toUpperCase()) return token;

  return token[0].toUpperCase();
};

/**
 * Compact a scheduled-OR label for the fixed-width card column.
 *
 * Names that already fit the column ("OR 1", "PACU 12", "Endo 1") are returned
 * unchanged, casing included. Longer descriptive names collapse alphabetic words
 * to concatenated initials and keep numeric identifiers: "Procedure Room 1" /
 * "proc room 1" → "PR 1", "Treatment Room 2" → "TR 2".
 *
 * "Room" is omitted when the preceding token was already a multi-letter acronym
 * so "OR Room 1" becomes "OR 1", not "ORR 1".
 *
 * Callers should keep the original name on `title` so hover still shows the full label.
 */
export const abbreviateScheduledOrName = (name: string): string => {
  if (!name) return '';

  const trimmed = name.trim();
  if (trimmed.length <= SCHEDULED_OR_MAX_UNABBREVIATED_LENGTH) return trimmed;

  const tokens = trimmed.match(/[A-Za-z]+|\d+/g);
  if (!tokens) return trimmed;

  const parts: string[] = [];
  let initials = '';
  let lastAbbrLength = 0;

  const flushInitials = () => {
    if (!initials) return;
    parts.push(initials);
    initials = '';
    lastAbbrLength = 0;
  };

  for (const token of tokens) {
    if (token[0] >= '0' && token[0] <= '9') {
      flushInitials();
      parts.push(token);
      continue;
    }

    if (ROOM_FILLER.has(token.toLowerCase()) && lastAbbrLength >= 2) {
      continue;
    }

    const abbr = abbreviateWord(token);
    initials += abbr;
    lastAbbrLength = abbr.length;
  }

  flushInitials();
  return parts.join(' ');
};

const timestamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const compareNullableTimestamps = (left: number | null, right: number | null): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
};

const compareRoomEntryThenId = (left: PatientCardPatient, right: PatientCardPatient): number => {
  const roomEntryComparison = compareNullableTimestamps(timestamp(left.changedRoomAt), timestamp(right.changedRoomAt));
  if (roomEntryComparison !== 0) return roomEntryComparison;

  return String(left.id ?? '').localeCompare(String(right.id ?? ''), undefined, { numeric: true });
};

export const sortPatientCards = <T extends PatientCardPatient>(
  patients: T[],
  order: PatientCardSortOrder | null | undefined
): T[] => {
  if (order !== 'SCHEDULED_START_ASC') return patients;

  return [...patients].sort((left, right) => {
    const scheduledStartComparison = compareNullableTimestamps(
      timestamp(getPatientCardProcedure(left)?.startTime),
      timestamp(getPatientCardProcedure(right)?.startTime)
    );

    return scheduledStartComparison || compareRoomEntryThenId(left, right);
  });
};
